import {
  LeaseClient, OutboxQueue, Replica, ReviewStore, Session,
  TokenFetchError, Transport, claimTabIdentity, sessionHoldTokens, type FieldSnapshot,
} from '@colplat/core-client';
import type { FieldVersion } from '@colplat/protocol';
import {
  escapeHtml, formString, mountCollabField, mountCollabToggle, renderRecordTable,
  type MountedField, type RecordTableRow,
} from '@colplat/ui-web';
import webledgerManifest from '@colplat/app-webledger';
import administrationManifest from '../../manifest.js';
import { apiFetch } from '../api.js';
import { appHref, globals, wsUrl } from '../globals.js';
import { getStoredSession, onSignedOutElsewhere, returnToLogin } from '../auth-storage.js';
import { accountMenuHtml, wireAccountMenu } from './accountMenu.js';
import { presenceUserFor } from '../identity.js';
import { forceUpdateNow } from '../serviceWorker.js';

/** Loaded via CDN `<script>`, same as ui-web's `mountEditModal` relies on. */
interface BootstrapModalLike { show(): void; hide(): void }
function bsModal(el: Element): BootstrapModalLike | undefined {
  return (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(e: Element): BootstrapModalLike } } })
    .bootstrap?.Modal.getOrCreateInstance(el);
}

interface RoleRow { id: string; appId: string; role: string; active: boolean }
interface UserRow { id: string; displayName: string; active: boolean; roles: RoleRow[] }

/**
 * The only apps a role grant may target — same reasoning as api's
 * `KNOWN_MANIFESTS` and sync's `MANIFESTS`: an app isn't grantable here until
 * it's real (has a manifest, is wired into api/sync). Extend this array as
 * each new app arrives; the roles table below groups by exactly this list,
 * so a new entry shows up as a new group with nothing to migrate.
 */
const KNOWN_APPS: ReadonlyArray<{ id: string; label: string; roles: readonly string[] }> = [
  { id: 'administration', label: 'Administration', roles: administrationManifest.roles ?? [] },
  { id: 'webledger', label: 'WebLedger', roles: webledgerManifest.roles ?? [] },
];

export async function mountAdministration(root: HTMLElement): Promise<void> {
  const stored = getStoredSession();
  if (!stored) {
    window.location.assign(appHref('/login'));
    return;
  }
  onSignedOutElsewhere(() => window.location.assign(appHref('/login')));
  const token = stored.token;
  const userId = stored.userId;

  root.innerHTML = shellHtml();
  root.querySelector('#backBtn')?.addEventListener('click', () => window.location.assign(appHref('/')));
  const themeSession = getStoredSession();
  if (themeSession) wireAccountMenu(root, themeSession.userId);

  const meRes = await apiFetch('/auth/me', { token });
  if (meRes.status === 401) {
    returnToLogin();
    return;
  }
  const me = (await meRes.json()) as { displayName: string };

  const listRes = await apiFetch('/admin/users', { token });
  if (listRes.status === 403) {
    root.querySelector('#adminBody')!.innerHTML = '<p class="text-secondary">You do not have access to this app.</p>';
    return;
  }
  let { users } = (await listRes.json()) as { users: UserRow[] };

  // Fresh per tab, in memory only — never localStorage (SPEC §3.3: two tabs
  // sharing a node id could collide two genuinely different writes).
  const node = crypto.randomUUID();

  const transport = new Transport({
    wsUrl,
    fetchToken: async () => {
      const res = await apiFetch('/auth/ws-token', { method: 'POST', token, body: { appId: 'administration' } });
      // `fatal` only when the server actually refused us: a 5xx or a rejected
      // `fetch` (offline) must stay retryable, or one drop is terminal.
      // See core-client's `TokenFetchError`.
      if (!res.ok) throw new TokenFetchError('could not mint ws token', res.status === 401 || res.status === 403);
      const data = (await res.json()) as { token: string };
      return { token: data.token, expiresInSeconds: 60 };
    },
    node,
    schemaVersion: administrationManifest.schemaVersion,
  });

  // Holds outlive the connection (ADR-0003, 2026-10-22) — see the webledger
  // bootstrap for why the tab's identity is settled first.
  await claimTabIdentity();
  // A page brought back from the back/forward cache left through `pagehide`,
  // which released its holds, and its socket is gone: load it afresh rather
  // than show a form that believes it still holds anything.
  window.addEventListener('pageshow', (event) => { if (event.persisted) window.location.reload(); });
  const leaseClient = new LeaseClient({
    transport,
    tokens: sessionHoldTokens(typeof sessionStorage === 'undefined' ? undefined : sessionStorage),
    releaseOnExit: (key, holdToken) => {
      void apiFetch('/admin/holds/release', {
        method: 'POST', token, keepalive: true, body: { holds: [{ key, token: holdToken }] },
      }).catch(() => undefined);
    },
  });
  const outbox = new OutboxQueue({ appId: 'administration', env: globals().env });
  await outbox.open();

  const replica = new Replica({
    appId: 'administration',
    env: globals().env,
    schemaVersion: administrationManifest.schemaVersion,
    fetchRecord: async (entity, recordId) => {
      const res = await apiFetch(`/admin/records/${entity}/${recordId}`, { token });
      if (!res.ok) return { fields: [] };
      return (await res.json()) as { fields: FieldSnapshot[]; history?: FieldVersion[] };
    },
  });
  await replica.open();

  // No document-mode entities in this build — pending revisions never occur.
  const review = new ReviewStore({
    fetchPendingRevisions: async () => [],
    postPendingRevisionResolution: async () => {},
  });

  const session = new Session({
    me: presenceUserFor(userId, me.displayName),
    manifest: administrationManifest,
    transport, leaseClient, outbox, replica, review, node,
    commitDocumentRest: async () => ({ ok: false, reason: 'validation' }),
  });

  // A rejected write (stale-base, no-right, the last-super-admin guard, etc.)
  // otherwise fails completely silently — `Session.notice()` exists
  // specifically to surface this, wired to the one `aria-live="polite"`
  // region on the page (hard invariant 11: never assertive).
  const liveRegion = root.querySelector<HTMLElement>('[aria-live]');
  session.onNotice((message) => { if (liveRegion) liveRegion.textContent = message; });

  // Same reasoning as webledgerClient.ts: a client `sync` has refused as too
  // old (ADR-0016) must not be left pinned to a cached shell (ADR-0022).
  transport.onSchemaStale(() => {
    if (liveRegion) liveRegion.textContent = 'This app has been updated and needs to reload.';
    forceUpdateNow();
  });

  transport.connect();

  // A record created by another tab (via `/admin/users` or the add-role
  // endpoint) has nothing to subscribe to until it exists — `sync` fans this
  // out to every connection for the app (not just record-level occupants;
  // see services/sync/src/handlers.ts's `broadcastToApp`), so this tab
  // refetches the list rather than trying to splice in a partial record.
  transport.onMessage((msg) => {
    if (msg.t === 'recordEvent' && (msg.entity === 'user' || msg.entity === 'roleGrant')) {
      void refresh();
    }
  });

  wireNewUserForm(root, token, refresh);
  render();

  async function refresh(): Promise<void> {
    const res = await apiFetch('/admin/users', { token });
    if (!res.ok) return;
    users = ((await res.json()) as { users: UserRow[] }).users;
    render();
  }

  function render(): void {
    renderUsersTable(root, session, users, token, refresh);
  }
}

function shellHtml(): string {
  return `
    <div class="container py-4">
      <div class="d-flex justify-content-between align-items-center mb-3 gap-2">
        <div class="d-flex align-items-center gap-2">
          <!-- The same control webledger puts here, in the same place and
               with the same words. It was "Back to apps" on the right, which
               is a different button doing the same job and reads as a
               different thing — the chevron for the affordance, the word for
               what it does, on the left where leaving goes. -->
          <button class="btn btn-sm btn-outline-secondary" type="button" id="backBtn">
            <span aria-hidden="true">&lsaquo;</span> Apps</button>
          <h1 class="h4 mb-0">Administration — Users</h1>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="modal" data-bs-target="#newUserModal">New user</button>
          ${accountMenuHtml({ signOut: false })}
        </div>
      </div>
      <div id="adminBody"><div class="cf-card cf-card--bleed p-3"><div id="usersTable"></div></div></div>
    </div>

    <div class="modal fade" id="userModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold" id="userModalTitle">User</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body pt-4" id="userModalFields"></div>
          <div class="modal-footer py-2 d-block">
            <div class="cf-h mb-2">Roles</div>
            <div id="userModalRoles"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="newUserModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">New user</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="newUserForm">
            <div class="modal-body">
              <div class="mb-2"><label class="form-label">Username</label><input class="form-control" name="username" required></div>
              <div class="mb-2"><label class="form-label">Display name</label><input class="form-control" name="displayName" required></div>
              <div class="mb-2"><label class="form-label">Temporary password</label>
                <input class="form-control" name="password" type="text" minlength="8" required></div>
            </div>
            <div class="modal-footer py-2">
              <button class="btn btn-primary btn-sm" type="submit">Create</button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
}

function renderUsersTable(root: HTMLElement, session: Session, users: UserRow[], token: string, onChanged: () => void | Promise<void>): void {
  const container = root.querySelector<HTMLElement>('#usersTable')!;
  const rows: RecordTableRow[] = users.map((u) => ({
    id: u.id,
    html:
      `<td class="cf-title"><button type="button" data-open-user="${u.id}">${escapeHtml(u.displayName)}</button></td>` +
      `<td>${u.active ? '<span class="badge text-bg-success">active</span>' : '<span class="badge text-bg-secondary">inactive</span>'}</td>` +
      `<td>${u.roles.filter((r) => r.active).map((r) => `<span class="badge text-bg-light border me-1">${escapeHtml(r.appId)}:${escapeHtml(r.role)}</span>`).join('') || '<span class="text-secondary">none</span>'}</td>`,
  }));
  renderRecordTable(container, { session, entity: 'user', headers: ['Name', 'Status', 'Roles'], rows });

  container.querySelectorAll<HTMLButtonElement>('[data-open-user]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const user = users.find((u) => u.id === btn.dataset['openUser']);
      if (user) openUserModal(root, session, user, token, onChanged);
    });
  });
}

function openUserModal(root: HTMLElement, session: Session, user: UserRow, token: string, onChanged: () => void | Promise<void>): void {
  const modalEl = root.querySelector<HTMLElement>('#userModal')!;
  const titleEl = root.querySelector<HTMLElement>('#userModalTitle')!;
  const fieldsEl = root.querySelector<HTMLElement>('#userModalFields')!;
  const rolesEl = root.querySelector<HTMLElement>('#userModalRoles')!;

  titleEl.textContent = user.displayName;
  fieldsEl.innerHTML = '<div id="fDisplayName" class="mb-2"></div><div id="fActive"></div>';

  let mounted: MountedField[] = [];

  /**
   * One group per known app (not per app the user happens to already hold a
   * grant on — SPEC's "administration offers a picker" needs the group to
   * exist before the first grant, so there's somewhere to add one). Each
   * existing grant's `active` cell is a real CollabToggle — a table cell is
   * just a container element as far as ui-web is concerned.
   */
  function renderRoles(): void {
    for (const m of mounted.splice(2)) m.destroy(); // keep [displayName, active]; drop old role toggles

    const sections = KNOWN_APPS.map((app) => {
      const grants = user.roles.filter((r) => r.appId === app.id);
      const rowsHtml = grants
        .map((r) => `<tr data-role-row="${r.id}"><td class="ps-3"><div id="roleToggle-${r.id}"></div></td></tr>`)
        .join('');

      const taken = new Set(grants.map((g) => g.role));
      const available = app.roles.filter((r) => !taken.has(r));
      let addRowHtml = '';
      if (app.roles.length === 0) {
        // No declared role vocabulary — falls back to free text (SPEC §4).
        addRowHtml = `<tr><td class="ps-3"><div class="d-flex gap-2 py-1">
          <input class="form-control form-control-sm" data-add-role-input data-app-id="${app.id}" placeholder="Role" style="max-width:160px">
          <button type="button" class="btn btn-sm btn-outline-secondary" data-add-role-btn data-app-id="${app.id}">Add</button>
        </div></td></tr>`;
      } else if (available.length > 0) {
        addRowHtml = `<tr><td class="ps-3"><div class="d-flex gap-2 py-1">
          <select class="form-select form-select-sm" data-add-role-select data-app-id="${app.id}" style="max-width:160px">
            ${available.map((r) => `<option value="${escapeHtml(r)}">${escapeHtml(r)}</option>`).join('')}
          </select>
          <button type="button" class="btn btn-sm btn-outline-secondary" data-add-role-btn data-app-id="${app.id}">Add</button>
        </div></td></tr>`;
      }

      return `<tr class="cf-role-group"><td>${escapeHtml(app.label)}</td></tr>${rowsHtml}${addRowHtml}`;
    }).join('');

    rolesEl.innerHTML = `<table class="table table-sm cf-table mb-0"><tbody>${sections}</tbody></table>`;

    for (const r of user.roles) {
      const container = rolesEl.querySelector<HTMLElement>(`#roleToggle-${r.id}`);
      if (!container) continue;
      mounted.push(mountCollabToggle(container, {
        session, entity: 'roleGrant', recordId: r.id, field: 'active', label: r.role,
      }));
    }

    rolesEl.querySelectorAll<HTMLButtonElement>('[data-add-role-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const appId = btn.dataset['appId']!;
        const select = rolesEl.querySelector<HTMLSelectElement>(`[data-add-role-select][data-app-id="${appId}"]`);
        const input = rolesEl.querySelector<HTMLInputElement>(`[data-add-role-input][data-app-id="${appId}"]`);
        const role = (select?.value ?? input?.value ?? '').trim();
        if (role) void addRole(appId, role);
      });
    });
  }

  async function addRole(appId: string, role: string): Promise<void> {
    const res = await apiFetch(`/admin/users/${user.id}/roles`, {
      method: 'POST', token, body: { id: crypto.randomUUID(), appId, role },
    });
    if (!res.ok) return;
    const created = (await res.json()) as { id: string };
    user.roles.push({ id: created.id, appId, role, active: true });
    await session.openRecord('roleGrant', created.id);
    renderRoles();
    void onChanged();
  }

  void (async () => {
    await session.openRecord('user', user.id);
    for (const r of user.roles) await session.openRecord('roleGrant', r.id);

    mounted.push(mountCollabField(fieldsEl.querySelector('#fDisplayName')!, {
      session, entity: 'user', recordId: user.id, field: 'displayName', label: 'Display name',
    }));
    mounted.push(mountCollabToggle(fieldsEl.querySelector('#fActive')!, {
      session, entity: 'user', recordId: user.id, field: 'active', label: 'Active',
    }));
    renderRoles();
    bsModal(modalEl)?.show();
  })();

  const onHidden = (): void => {
    session.closeRecord('user', user.id);
    for (const r of user.roles) session.closeRecord('roleGrant', r.id);
    for (const m of mounted) m.destroy();
    mounted = [];
    modalEl.removeEventListener('hidden.bs.modal', onHidden);
    void onChanged();
  };
  modalEl.addEventListener('hidden.bs.modal', onHidden);
}

function wireNewUserForm(root: HTMLElement, token: string, onCreated: () => void | Promise<void>): void {
  const form = root.querySelector<HTMLFormElement>('#newUserForm')!;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const username = formString(data, 'username').trim();
      const displayName = formString(data, 'displayName').trim();
      const password = formString(data, 'password');
      if (!username || !displayName || password.length < 8) return;

      const res = await apiFetch('/admin/users', {
        method: 'POST', token,
        body: { id: crypto.randomUUID(), username, displayName, password, roles: [] },
      });
      if (res.ok) {
        form.reset();
        const modalEl = root.querySelector<HTMLElement>('#newUserModal')!;
        bsModal(modalEl)?.hide();
        void onCreated();
      }
    })();
  });
}
